// Pieces of the home screen check shared by the test page and the first-time setup:
// a failed check with its click-for-click fix, and the "Check my monitor" download.

import { useState } from "react";
import { api, type MonitorReport, type QualifyCheck } from "../api";

/** Checks the helper download answers: run it (again) to fix them. */
export const HELPER_KEYS = ["monitor_id", "monitor_model_edid", "monitor_serial", "monitor_bits", "link_bits"];

/** One failed check: what is wrong, then numbered steps for this computer. */
export function FixCheck({ check: c, open }: { check: QualifyCheck; open: boolean }) {
  const address = `${window.location.origin}/`;
  const [copied, setCopied] = useState(false);
  const body = (
    <>
      <p style={{ margin: "0.3rem 0" }}>{c.fix}</p>
      {c.steps && c.steps.length > 0 && (
        <ol className="steps fix-steps">
          {c.steps.map((step, i) => (
            <li key={i}>{step.split("{address}").flatMap((part, j) => (j === 0 ? [part]
              : [<strong key={j} className="address">{address}</strong>, part]))}</li>
          ))}
        </ol>
      )}
      {c.key === "browser" && navigator.clipboard && (
        <p><button className="secondary" onClick={() => void navigator.clipboard.writeText(address).then(() => setCopied(true))}>
          {copied ? "Copied: now paste it into Chrome" : "Copy this page’s address"}</button></p>
      )}
      <div className="small muted">What the check saw: {c.detail}</div>
    </>
  );
  return open ? (
    <section className="fix-check"><h3>{c.label}</h3>{body}</section>
  ) : (
    <details className="fix-check"><summary><strong>{c.label}</strong></summary>{body}</details>
  );
}

export function detectPlatform(): "mac" | "windows" | "other" {
  const ua = navigator.userAgent;
  if (/Macintosh|Mac OS X/.test(ua)) return "mac";
  if (/Windows/.test(ua)) return "windows";
  return "other";
}

/** One-time: download the small helper that reads every connected monitor's EDID and
 *  sends it in. The screen check then picks the monitor this window is on. */
export function MonitorHelper({ required, report, steps, onDone, busy }: {
  required: boolean; report: MonitorReport | null; steps: Partial<Record<"mac" | "windows", string[]>>;
  onDone: () => void; busy: boolean;
}) {
  const plat = detectPlatform();
  const [skipped, setSkipped] = useState(false);
  if (skipped) return null;
  const forPlatform = (p: "mac" | "windows", label: string) => (
    <>
      <p><a className="button big" href={api.monitorHelperUrl(p)}>{label}</a></p>
      <ol className="steps fix-steps">{(steps[p] ?? []).map((st, i) => <li key={i}>{st}</li>)}</ol>
    </>
  );
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Identify your monitor (one time)</h2>
      <p>This small download reads the built-in identity of every monitor connected to this computer (maker, model,
        serial number, size, and how many bits of colour the computer sends it) and sends it to the study. It changes
        nothing on your computer. Keep the monitor you test on connected while it runs.</p>
      {plat !== "windows" && forPlatform("mac", "Download for Mac")}
      {plat !== "mac" && forPlatform("windows", "Download for Windows")}
      {report && report.monitors.length > 0 && (
        <div className="small">
          <p style={{ marginBottom: "0.2rem" }}>Found last time ({new Date(report.created_at).toLocaleString()}):</p>
          <ul style={{ marginTop: 0 }}>
            {report.monitors.map((m, k) => <li key={k}>{[m.manufacturer, m.capabilities?.name].filter(Boolean).join(" ") || "a monitor"}
              {m.serial_text || m.serial_number ? `, serial ${m.serial_text || m.serial_number}` : ""}</li>)}
          </ul>
          <p className="muted">None of these is the screen this window is on. Move this window onto the monitor you test
            on, or connect it, and run the download again.</p>
        </div>
      )}
      <p className="small muted">Each download works once, for 30 minutes. The study team can also do this step for you
        when they set up your monitor.</p>
      <div className="row">
        <button className="big" disabled={busy} onClick={onDone}>I’ve done it: check again</button>
        {!required && <button className="secondary" onClick={() => setSkipped(true)}>Skip for now</button>}
      </div>
    </div>
  );
}
